'use client';

import type { ReactNode } from 'react';
import { ChevronDown, Plug, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  CONNECTION_APPLICATION_STATUSES,
  CONNECTION_STATUS_LABELS,
  SERVICE_REQUEST_STATUSES,
  SERVICE_STATUS_LABELS,
} from '@/lib/request-type-status-config';

export interface ConnectionServiceStatusFilterProps {
  connectionStatuses: Set<string>;
  serviceStatuses: Set<string>;
  onConnectionStatusesChange: (next: Set<string>) => void;
  onServiceStatusesChange: (next: Set<string>) => void;
  /** Доп. блок справа (напр. «Созданные / Назначенные» на календаре) */
  trailing?: ReactNode;
  className?: string;
  compact?: boolean;
  /** Подпись слева «Тип заявки» (на карте обычно скрывают) */
  showTypeLabel?: boolean;
  /** Только статусы подключения (список заявок на подключение) */
  connectionOnly?: boolean;
  /** Только статусы сервиса (список сервисных заявок) */
  serviceOnly?: boolean;
}

function toggleInSet(set: Set<string>, key: string, checked: boolean): Set<string> {
  const next = new Set(set);
  if (checked) next.add(key);
  else next.delete(key);
  return next;
}

export function ConnectionServiceStatusFilter({
  connectionStatuses,
  serviceStatuses,
  onConnectionStatusesChange,
  onServiceStatusesChange,
  trailing,
  className,
  compact,
  showTypeLabel = true,
  connectionOnly = false,
  serviceOnly = false,
}: ConnectionServiceStatusFilterProps) {
  const connTotal = CONNECTION_APPLICATION_STATUSES.length;
  const srvTotal = SERVICE_REQUEST_STATUSES.length;
  const connActive = connectionStatuses.size;
  const srvActive = serviceStatuses.size;
  const showConn = !serviceOnly;
  const showSrv = !connectionOnly;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2',
        compact ? '' : 'rounded-lg border border-border bg-card/50 px-3 py-2',
        className
      )}
    >
      {showTypeLabel ? (
        <span className="shrink-0 text-xs font-medium text-muted-foreground">Тип заявки</span>
      ) : null}

      {showConn ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="group h-10 min-w-[10.5rem] justify-between gap-2 px-3"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Plug className="size-4 shrink-0 text-[#15803d]" aria-hidden />
              <span className="truncate">Подключение</span>
              <Badge variant="secondary" className="shrink-0 font-normal tabular-nums">
                {connActive}/{connTotal}
              </Badge>
            </span>
            <ChevronDown
              className="size-4 shrink-0 text-muted-foreground opacity-70 transition-transform duration-200 group-data-[state=open]:rotate-180"
              aria-hidden
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[min(100vw-2rem,17rem)]">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Выберите статусы подключения
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {CONNECTION_APPLICATION_STATUSES.map((st) => (
            <DropdownMenuCheckboxItem
              key={st}
              checked={connectionStatuses.has(st)}
              onCheckedChange={(checked) =>
                onConnectionStatusesChange(toggleInSet(connectionStatuses, st, !!checked))
              }
              onSelect={(e) => e.preventDefault()}
            >
              {CONNECTION_STATUS_LABELS[st] ?? st}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      ) : null}

      {showSrv ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="group h-10 min-w-[9rem] justify-between gap-2 px-3"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Wrench className="size-4 shrink-0 text-primary" aria-hidden />
              <span className="truncate">Сервис</span>
              <Badge variant="secondary" className="shrink-0 font-normal tabular-nums">
                {srvActive}/{srvTotal}
              </Badge>
            </span>
            <ChevronDown
              className="size-4 shrink-0 text-muted-foreground opacity-70 transition-transform duration-200 group-data-[state=open]:rotate-180"
              aria-hidden
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[min(100vw-2rem,17rem)]">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Выберите статусы сервиса
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {SERVICE_REQUEST_STATUSES.map((st) => (
            <DropdownMenuCheckboxItem
              key={st}
              checked={serviceStatuses.has(st)}
              onCheckedChange={(checked) =>
                onServiceStatusesChange(toggleInSet(serviceStatuses, st, !!checked))
              }
              onSelect={(e) => e.preventDefault()}
            >
              {SERVICE_STATUS_LABELS[st] ?? st}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      ) : null}

      {trailing ? <div className="flex flex-wrap items-center gap-2">{trailing}</div> : null}
    </div>
  );
}
